import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import { useTranslationApp } from "../../../../lib/i18next";
import {
  AD,
  AD_GROUP,
  ASSETS_GROUP,
  CAMPAIGN_APPROVAL_STALE_LABEL,
  FIELD,
  UNKNOWN,
} from "../../../../i18n/keysTranslations";
import { INPUT_COLOR_BORDER } from "../../../../utils/colors";
import { logoPlatform } from "../../../../utils/string";

const ENTITY_LABEL_KEYS = {
  adGroup: AD_GROUP,
  ad: AD,
  assetsGroup: ASSETS_GROUP,
  field: FIELD,
};

const EntityLine = ({ type, name, sx = {} }) => {
  const { t } = useTranslationApp();
  const labelKey = ENTITY_LABEL_KEYS[type];

  if (!labelKey) {
    return null;
  }

  const displayName = name?.trim() ? name.trim() : t(UNKNOWN);

  return (
    <Typography
      variant="caption"
      sx={{ display: "block", color: "text.secondary", lineHeight: 1.5, ...sx }}
    >
      {t(labelKey)}:{" "}
      <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
        {displayName}
      </Box>
    </Typography>
  );
};

const CampaignApprovalCommentCard = ({
  body = "",
  headerLabel = "",
  reviewerEmail = "",
  entityLines = [],
  fieldLabel = "",
  platform = "",
  isSelected = false,
  isStale = false,
  isNavigable = false,
  variant = "specific",
  onClick,
}) => {
  const { t } = useTranslationApp();
  const borderColor = isSelected ? "#000" : INPUT_COLOR_BORDER;
  const authorLabel = reviewerEmail?.trim?.() || "";

  if (variant === "general") {
    return (
      <Card
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor,
          borderRadius: 2,
          p: 1.5,
          opacity: isStale ? 0.6 : 1,
        }}
      >
        {headerLabel ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 0.75, fontWeight: 600 }}
          >
            {headerLabel}
          </Typography>
        ) : null}
        {authorLabel ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 0.75, fontWeight: 600 }}
          >
            {authorLabel}
          </Typography>
        ) : null}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 1,
            alignItems: "flex-start",
          }}
        >
          <Typography
            variant="body2"
            sx={{ whiteSpace: "pre-wrap", color: "text.primary", flex: 1 }}
          >
            {body}
          </Typography>

          {isStale ? (
            <Typography
              variant="caption"
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                textTransform: "uppercase",
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {t(CAMPAIGN_APPROVAL_STALE_LABEL)}
            </Typography>
          ) : null}
        </Box>
      </Card>
    );
  }

  return (
    <Card
      elevation={0}
      onClick={isNavigable ? onClick : undefined}
      sx={{
        border: "1px solid",
        borderColor,
        borderRadius: 2,
        p: 1.5,
        cursor: isNavigable ? "pointer" : "default",
        opacity: isStale ? 0.6 : 1,
        transition: "border-color 0.2s ease",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 1,
          alignItems: "flex-start",
        }}
      >
        <Box sx={{ display: "flex", gap: 1, minWidth: 0, flex: 1 }}>
          {platform ? (
            <Avatar
              src={logoPlatform(platform)}
              variant="square"
              sx={{
                width: 24,
                height: 24,
                flexShrink: 0,
                bgcolor: "transparent",
                "& .MuiAvatar-img": {
                  objectFit: "contain",
                },
              }}
            />
          ) : null}

          <Box sx={{ minWidth: 0, flex: 1 }}>
            {entityLines.length > 0 ? (
              <Box>
                {entityLines.map((line) => (
                  <EntityLine
                    key={line.type}
                    type={line.type}
                    name={line.name}
                  />
                ))}
              </Box>
            ) : null}

            {fieldLabel ? (
              <EntityLine
                key={"field"}
                type={"field"}
                name={fieldLabel}
                sx={{ mb: 0.75 }}
              />
            ) : null}

            {authorLabel ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.75, fontWeight: 600 }}
              >
                {authorLabel}
              </Typography>
            ) : null}

            <Typography
              variant="body2"
              sx={{
                whiteSpace: "pre-wrap",
                color: "text.primary",
              }}
            >
              {body}
            </Typography>
          </Box>
        </Box>

        {isStale ? (
          <Typography
            variant="caption"
            sx={{
              px: 1,
              py: 0.25,
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
              textTransform: "uppercase",
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {t(CAMPAIGN_APPROVAL_STALE_LABEL)}
          </Typography>
        ) : null}
      </Box>
    </Card>
  );
};

export default CampaignApprovalCommentCard;
